import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { convertFormDateValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { convertKnmDocumentAttachmentsFormValuesToKnmData } from 'KnmPackage/MainModule/common/convertKnmDocumentAttachmentsFormValuesToKnmData';
import { convertViolatedRequirementsFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertViolatedRequirementsFormValuesToKnmData';
import { IKnmAct248 } from 'APIPackage/KnmModule/interfaces/IKnmAct248';
import { convertFormDateTimeValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateTimeValueToKnmDataDate';
import { getIntNumberByString } from 'CorePackage/MainModule/common/getIntNumberByString';
import { convertEventsFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertEventsFormValuesToKnmData';
import { convertInspectorsFormValuesToKnmData } from 'KnmErknmPackage/MainModule/common/convertInspectorsFormValuesToKnmData';
import { convertActExpertsFormValueToKnmData } from 'KnmErknmPackage/MainModule/common/convertActExpertsFormValueToKnmData';
import { IKnmActFamiliarizationNotifications } from 'APIPackage/KnmModule/interfaces/IKnmActFamiliarizationNotifications';
import { store } from 'CorePackage/MainModule/common/store';
import { getDirtyActGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { convertActRemarksFormToData } from 'KnmErknmPackage/MainModule/common/convertActRemarksFormToData';

import { IKnmPmFormAct } from '../interfaces/IKnmPmFormAct';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IKnmErknmFormSuspensionEvent } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';

export const convertPmActFormToKnmData = (
  act?: IKnmPmFormAct | undefined,
  suspensionEvents?: IKnmErknmFormSuspensionEvent[],
): IKnmAct248 | null => {
  if (!act) return null;

  const state = store.getState();
  const dirtyActGuid = getDirtyActGuid(state);

  return {
    acquaintanceLackNoInfoComment: act.acquaintanceLackNoInfoComment || null,
    acquaintanceLackType: act.acquaintanceLackType
      ? deleteObjectProperties(act.acquaintanceLackType, ['label', 'value'])
      : null,
    acquaintanceType: act.acquaintanceType ? deleteObjectProperties(act.acquaintanceType, ['label', 'value']) : null,
    canceledReason: act.canceledReason || null,
    canceledReasonDescription: act.canceledReasonDescription || null,
    canceledReasonDescriptionReactQuillEditor: handleReactQuillDeltaValue(
      act.canceledReasonDescriptionReactQuillEditor || act.canceledReasonDescription || null,
    ),
    created: act.created || null,
    createdBy: act.createdBy || null,
    creationActDateTime: convertFormDateTimeValueToKnmDataDate(act.creationActDateTime),
    signActDateTime: act.signActDateTime || null,
    document: !act.document
      ? null
      : {
          active: act.document?.active || null,
          attachments: convertKnmDocumentAttachmentsFormValuesToKnmData(act.document?.attachments),
          correlationToken: act.document?.correlationToken || null,
          description: act.document?.description || null,
          guid: act.document?.guid || null,
          type: act.document?.type || null,
        },
    durationDays: getIntNumberByString(act?.durationDays),
    durationHours: getIntNumberByString(act?.durationHours),
    events: convertEventsFormValuesToKnmData(act.events, suspensionEvents),
    fioReader: act.fioReader || null,
    fioSigner: act?.fioAndPositionSinger?.value.fullName || null,
    guid: act.guid || null,
    holdingDateTime: convertFormDateTimeValueToKnmDataDate(act.holdingDateTime),
    isViolationResolved: typeof act.isViolationResolved?.value === 'boolean' ? act.isViolationResolved.value : null,
    isViolationResolvedNote: act.isViolationResolvedNote || null,
    isViolations:
      typeof act.isViolatedMandatoryRequirements?.value === 'boolean'
        ? act.isViolatedMandatoryRequirements.value
        : null,
    knoInspectors: convertInspectorsFormValuesToKnmData(true, act.knoInspectors),
    involvedExperts: convertActExpertsFormValueToKnmData(act.involvedExperts),
    involvedWitnesses: !act.involvedWitnesses
      ? null
      : act.involvedWitnesses.filter(i => i.fullName).map(i => ({ fullName: i.fullName || '', guid: i.listId })),
    nextWordDayActDateTime: act.nextWordDayActDateTime || null,
    noteInvalidate: act.noteInvalidate || null,
    fileInvalidate: convertKnmDocumentAttachmentsFormValuesToKnmData([act.fileInvalidate])?.pop() || null,
    numberAct: act.numberAct || null,
    refuserFio: act.refuserFio || null,
    refuserTitle: act.refuserTitle || null,
    canceled: act.canceled || null,
    titleReader: act.titleReader || null,
    titleSigner: act?.fioAndPositionSinger?.value.position?.value || null,
    typeAct: act.typeAct || null,
    updated: act.updated || null,
    updatedBy: act.updatedBy || null,
    violations: convertViolatedRequirementsFormValuesToKnmData(act.violatedMandatoryRequirements),
    familiarizationNotifications:
      act?.familiarizationNotifications?.map(x => {
        const notification: IKnmActFamiliarizationNotifications = {
          noticeDate: convertFormDateValueToKnmDataDate(x.noticeDate),
          knmNoticeType: x.knmNoticeType || null,
          note: x.note || null,
          guid: x.listId,
          isNew: x.isNew,
        };

        return notification;
      }) || null,
    remarks: convertActRemarksFormToData(act.remarks),
    signed: dirtyActGuid && dirtyActGuid === act.guid ? false : act.signed ?? null,
    paperless: act.paperless || null,
    extractVersion: act.extractVersion || null,
    officialFullNameCancel: act.officialFullNameCancel || null,
    resultContentPositionCancel: !act.resultContentPositionCancel ? null : act.resultContentPositionCancel.value,
    firstExtractDateTime: convertFormDateTimeValueToKnmDataDate(act.firstExtractDateTime) || null,
    isNew: act.isNew || null,
    systemSignatureUsed: act?.systemSignatureUsed || false,
    systemSignatureDateTime: act?.systemSignatureDateTime || null,
    systemSignatureMnemonic: act?.systemSignatureMnemonic || null,
    systemSignatureTitle: act?.systemSignatureTitle || null,
  };
};
